// src/components/OnboardingGuide.jsx
// 4-step educational overlay shown once after account setup completes.
// Teaches new users what the app is, how to use it, and sets language preference.
// Gated by settings.onboardingGuideDone — never shows again once dismissed.

import { useState } from 'react'

// ── Step content ──────────────────────────────────────────────────────────────

const STEPS = [
  {
    id:    'framing',
    index: 1,
    icon:  '◈',

    finance: {
      title:    'Data-driven portfolio analysis',
      subtitle: 'Not speculation. Not gambling.',
      body:     'Nuvport OS gives you institutional-grade tools to analyse your holdings using real financial data — geopolitical risk scores, sector concentration metrics, valuation multiples, and Monte Carlo simulations.',
    },
    plain: {
      title:    'Markets are not gambling — this is data analysis',
      subtitle: 'Real numbers. Real decisions.',
      body:     "Investing isn't about guessing. It's about using real information to make better decisions. Nuvport OS puts the same tools used by professional investors directly in your hands — in plain language, without the $2,000/month price tag.",
    },
  },
  {
    id:    'dashboard',
    index: 2,
    icon:  '◉',

    finance: {
      title:    'Your Dashboard',
      subtitle: 'Portfolio health at a glance.',
      body:     'The Dashboard shows your portfolio value, daily P&L, risk score, and geo-political exposure. The Daily Intelligence Card surfaces your primary risk driver and a recommended action. Morning Brief gives you a full portfolio digest each day.',
    },
    plain: {
      title:    'Your Dashboard',
      subtitle: "Everything important in one place.",
      body:     "The Dashboard shows how much your portfolio is worth, whether it went up or down today, and whether any of your investments are carrying hidden risks — like being too concentrated in one industry, or exposed to unstable regions of the world.",
    },
  },
  {
    id:    'holdings',
    index: 3,
    icon:  '⊕',

    finance: {
      title:    'Building your portfolio',
      subtitle: 'Add holdings to unlock all analysis.',
      body:     'Go to Portfolio Builder → search a ticker → set your weight or quantity. Once you have holdings, every module personalises to your specific positions: risk scores, backtest performance, valuation screens, and geo-risk exposure all reflect your actual portfolio.',
    },
    plain: {
      title:    'Adding your investments',
      subtitle: 'Tell us what you own.',
      body:     "Go to Portfolio Builder and search for any stock, ETF, or crypto you own. Add it to your portfolio. Once you've added your holdings, every part of the app becomes personalised to you — it shows your risks, your performance, your opportunities.",
    },
  },
  {
    id:    'start',
    index: 4,
    icon:  '→',

    finance: {
      title:    'Start with the Dashboard',
      subtitle: 'Your command centre.',
      body:     'Head to the Dashboard first. Add your holdings in Portfolio Builder, then explore Valuation, Optimizer, and the Geo-Risk Map. The Daily Intelligence Card tells you where to focus each morning.',
    },
    plain: {
      title:    'Start here: the Dashboard',
      subtitle: "Your home base.",
      body:     "Start on the Dashboard. Add your first holding in Portfolio Builder — even one stock is enough to see the app come alive. Then explore from there. The app will tell you what to look at and why it matters for your specific investments.",
    },
  },
]

// ── Sub-components ────────────────────────────────────────────────────────────

function LanguageToggle({ value, onChange }) {
  return (
    <div style={{
      display: 'inline-flex',
      background: '#0d1117',
      border: '1px solid #1c2333',
      borderRadius: 8,
      padding: 3,
      gap: 2,
    }}>
      {[
        { id: 'finance', label: 'Finance Mode' },
        { id: 'plain',   label: 'Plain English' },
      ].map(opt => (
        <button
          key={opt.id}
          onClick={() => onChange(opt.id)}
          style={{
            background: value === opt.id ? 'var(--accent, #3ddc84)' : 'transparent',
            border: 'none',
            borderRadius: 6,
            padding: '6px 14px',
            fontFamily: 'var(--font-mono, JetBrains Mono, monospace)',
            fontSize: 10,
            fontWeight: 700,
            color: value === opt.id ? '#0a0a0a' : '#8b949e',
            cursor: 'pointer',
            letterSpacing: '.06em',
            transition: 'all .15s',
            whiteSpace: 'nowrap',
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function ProgressDots({ total, current }) {
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          style={{
            width: i === current ? 20 : 6,
            height: 6,
            borderRadius: 3,
            background: i === current ? 'var(--accent, #3ddc84)' : '#1c2333',
            transition: 'width .2s, background .2s',
          }}
        />
      ))}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function OnboardingGuide({ onComplete, initialLanguageMode = 'plain' }) {
  const [step,     setStep]     = useState(0)
  const [langMode, setLangMode] = useState(initialLanguageMode)

  const current = STEPS[step]
  const content = langMode === 'plain' ? current.plain : current.finance
  const isLast  = step === STEPS.length - 1

  function next() {
    if (isLast) {
      onComplete(langMode)
    } else {
      setStep(s => s + 1)
    }
  }

  function skip() {
    onComplete(langMode)
  }

  return (
    <>
      <style>{`
        @keyframes og-fadein {
          from { opacity: 0; transform: scale(.97); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes og-step {
          from { opacity: 0; transform: translateX(16px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>

      {/* Backdrop */}
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.7)',
        zIndex: 8000,
        backdropFilter: 'blur(2px)',
      }} />

      {/* Card */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 8001,
        width: '100%',
        maxWidth: 500,
        padding: '0 16px',
        animation: 'og-fadein .3s ease',
      }}>
        <div style={{
          background: '#0d1117',
          border: '1px solid #1c2333',
          borderTop: '2px solid var(--accent, #3ddc84)',
          borderRadius: 16,
          overflow: 'hidden',
        }}>

          {/* Top bar — language toggle + skip */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            borderBottom: '1px solid #1a2332',
          }}>
            <LanguageToggle value={langMode} onChange={setLangMode} />
            <button
              onClick={skip}
              style={{
                background: 'none',
                border: 'none',
                color: '#8b949e',
                fontFamily: 'var(--font-mono, JetBrains Mono, monospace)',
                fontSize: 10,
                cursor: 'pointer',
                letterSpacing: '.08em',
                padding: '4px 0',
              }}
            >
              Skip intro
            </button>
          </div>

          {/* Step content */}
          <div
            key={step}
            style={{
              padding: '32px 32px 24px',
              animation: 'og-step .25s ease',
            }}
          >
            {/* Step number + icon */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 20,
            }}>
              <span style={{
                fontFamily: 'var(--font-mono, JetBrains Mono, monospace)',
                fontSize: 24,
                color: 'var(--accent, #3ddc84)',
                lineHeight: 1,
              }}>
                {current.icon}
              </span>
              <span style={{
                fontFamily: 'var(--font-mono, JetBrains Mono, monospace)',
                fontSize: 9,
                color: '#8b949e',
                letterSpacing: '.16em',
                textTransform: 'uppercase',
              }}>
                Step {current.index} of {STEPS.length}
              </span>
            </div>

            {/* Title */}
            <div style={{
              fontFamily: 'var(--font-display, Syne, sans-serif)',
              fontSize: 22,
              fontWeight: 700,
              color: '#c9d1d9',
              lineHeight: 1.25,
              marginBottom: 6,
            }}>
              {content.title}
            </div>

            {/* Subtitle */}
            <div style={{
              fontFamily: 'var(--font-mono, JetBrains Mono, monospace)',
              fontSize: 11,
              color: 'var(--accent, #3ddc84)',
              letterSpacing: '.06em',
              marginBottom: 16,
            }}>
              {content.subtitle}
            </div>

            {/* Body */}
            <div style={{
              fontFamily: 'var(--font-body, IBM Plex Sans, sans-serif)',
              fontSize: 13,
              color: '#8b949e',
              lineHeight: 1.7,
              marginBottom: 28,
            }}>
              {content.body}
            </div>

            {/* Progress dots */}
            <div style={{ marginBottom: 20 }}>
              <ProgressDots total={STEPS.length} current={step} />
            </div>

            {/* CTA button */}
            <button
              onClick={next}
              style={{
                width: '100%',
                background: 'var(--accent, #3ddc84)',
                border: 'none',
                borderRadius: 10,
                padding: '13px 0',
                fontFamily: 'var(--font-mono, JetBrains Mono, monospace)',
                fontSize: 12,
                fontWeight: 700,
                color: '#060a0f',
                cursor: 'pointer',
                letterSpacing: '.08em',
                transition: 'opacity .15s',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '.85'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              {isLast ? 'GO TO MY DASHBOARD →' : 'NEXT →'}
            </button>
          </div>

        </div>
      </div>
    </>
  )
}
